import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/common";
import { BpImport } from "../../components/bp/BpImport";
import { BR, FySelect, History, Section, fmtL, fmtN, fmtP, sum, r2, num, useBpMeta } from "../../components/bp/BpKit";
import { MonthGrid } from "../../components/bp/MonthGrid";
import { QuickFill } from "../../components/bp/QuickFill";
import { ApprovalBar } from "../../components/bp/ApprovalBar";
import { usePlan } from "../../components/bp/usePlan";

const toData = (x) => ({ months: x?.months || {}, categories: x?.categories || {} });

const Switcher = ({ branches, value, onPick }) => (
  <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="bp-branch-editor-switcher">
    {branches.map((b) => (
      <button key={b} type="button" data-testid={`bp-branch-tab-${b}`} onClick={() => onPick(b)}
        className={`h-9 px-4 text-sm transition-colors ${value === b ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{BR[b]}</button>
    ))}
  </div>
);

const Fact = ({ label, value, tid, tone }) => (
  <div className="bg-white border border-slate-300 rounded-sm px-4 py-3" data-testid={tid}>
    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="num text-xl font-bold mt-1" style={{ color: tone }}>{value}</div>
  </div>
);

const Facts = ({ data, ly, team }) => {
  const t = sum(data.months.bookings), l = sum(ly.bookings), tm = sum(team);
  const split = sum(data.months.new) + sum(data.months.repeat), cats = Object.values(data.categories).reduce((a, r) => a + sum(r), 0);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <Fact tid="bp-branch-fact-target" label="FY bookings plan" value={fmtL(r2(t))} />
      <Fact tid="bp-branch-fact-ly" label="Last year actual" value={fmtL(r2(l))} />
      <Fact tid="bp-branch-fact-growth" label="Growth on last year" value={l ? fmtP(Math.round(((t - l) / l) * 100)) : "—"} tone={t >= l ? "#059669" : "#e11d48"} />
      <Fact tid="bp-branch-fact-team" label="Salespeople's plans" value={fmtL(r2(tm))} tone={tm && Math.abs(tm - t) > 0.5 ? "#d97706" : undefined} />
      <Fact tid="bp-branch-fact-check" label="New + repeat · categories" value={`${fmtN(r2(split))} · ${fmtN(r2(cats))}`} tone={(split && Math.abs(split - t) > 0.5) || (cats && Math.abs(cats - t) > 0.5) ? "#d97706" : undefined} />
    </div>
  );
};

export default function BranchPlan() {
  const [sp, setSp] = useSearchParams();
  const meta = useBpMeta(sp.get("fy") || undefined);
  const branch = sp.get("branch") || meta?.branches?.[0];
  const { d, data, dirty, busy, edit, save, act } = usePlan(meta && meta.scope !== "own" && branch ? `/bp/branch/${branch}` : null, meta?.fy, toData);
  const set = (k, v) => setSp((p) => { const n = new URLSearchParams(p); n.set(k, v); return n; });
  const months = meta?.months || [];
  const metricRows = useMemo(() => (data && d ? [
    ...Object.entries(meta.metrics).map(([k, label]) => ({ key: k, label, values: data.months[k] || {} })),
    { key: "ly_bookings", label: "Last year PO bookings", values: d.ly.bookings, ref: true },
    { key: "team", label: "Salespeople's plans (total)", values: d.team, ref: true },
  ] : []), [data, d, meta]);
  if (!meta) return null;
  if (meta.scope === "own") return <PageHeader eyebrow="BP & Planning" title="Branch business plan" desc="Branch plans are drafted by branch heads and approved by founders. Open My sales plan to set your own targets." />;
  const locked = !d?.can.edit || ["submitted", "approved"].includes(d?.plan.status);
  const setCell = (group) => (k, m, v) => edit((x) => ({ ...x, [group]: { ...x[group], [k]: { ...(x[group][k] || {}), [m]: v } } }));
  const clean = (g) => Object.fromEntries(Object.entries(g).map(([k, r]) => [k, Object.fromEntries(Object.entries(r).filter(([, v]) => v !== "" && v != null).map(([m, v]) => [m, num(v)]))]));
  const catRows = meta.categories.map((c) => ({ key: c, label: c, values: data?.categories[c] || {} }));
  return (
    <div className="space-y-6" data-testid="bp-branch-page">
      <PageHeader eyebrow="BP & Planning" title="Branch business plan" desc="Set the year's monthly targets for the branch. Branch heads draft and submit; founders approve. Values in ₹ Lakhs before GST.">
        <FySelect meta={meta} value={meta.fy} onChange={(v) => set("fy", v)} />
        <Switcher branches={meta.branches} value={branch} onPick={(b) => set("branch", b)} />
        {meta.can.approve_branch && <BpImport fy={meta.fy} onDone={() => window.location.reload()} />}
      </PageHeader>
      {d && data && (
        <>
          <ApprovalBar plan={d.plan} can={d.can} dirty={dirty} busy={busy} prefix="bp-branch" submitLabel="Submit to founders"
            onSave={() => save({ months: clean(data.months), categories: clean(data.categories) })} onAct={act} />
          <Facts data={data} ly={d.ly} team={d.team} />
          <Section tid="bp-branch-monthly" title={`${d.label} · monthly targets`} hint="Bookings are PO values booked in the month. New and repeat should add up to bookings. Grey rows are for reference.">
            {!locked && <div className="mb-3"><QuickFill rows={metricRows.filter((r) => !r.ref)} months={months} ly={d.ly} onApply={(k, vals) => edit((x) => ({ ...x, months: { ...x.months, [k]: vals } }))} /></div>}
            <MonthGrid tid="bp-branch-monthly-grid-table" months={months} rows={metricRows} onCell={setCell("months")} readOnly={locked} />
          </Section>
          <Section tid="bp-branch-categories" title="By product category" hint="Split the bookings target across product categories. The total row should match PO bookings.">
            {!locked && catRows.length > 0 && <div className="mb-3"><QuickFill tid="bp-cat-quick-fill" rows={catRows} months={months} ly={d.ly_categories} onApply={(k, vals) => edit((x) => ({ ...x, categories: { ...x.categories, [k]: vals } }))} /></div>}
            <MonthGrid tid="bp-branch-category-grid-table" months={months} rows={catRows} onCell={setCell("categories")} readOnly={locked} totalLabel="All categories" />
          </Section>
          <History rows={d.plan.history} />
        </>
      )}
    </div>
  );
}
